import React from 'react';
import {
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { DEFAULT_CHIP_SX } from '../../theme';
import {
  getColorForCompanyStatus,
  getLabelForCompanyStatus,
} from '../../utils/company-status';

interface SearchResultItemLayoutProps {
  name: string;
  alternateName?: string | null;
  address?: string | null;
  status: string | null;
  statusDescription: string | null;
  companyId: string | null;
  officeType?: string | null;
  erpId?: string | null;
  crmId?: string | null;
  actions?: React.ReactNode;
}

const SearchResultItemLayout: React.FC<SearchResultItemLayoutProps> = ({
  name,
  alternateName = null,
  address = null,
  status,
  statusDescription,
  companyId,
  officeType = null,
  erpId = null,
  crmId = null,
  actions = null,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'search-result-item-layout' });
  const statusColor = getColorForCompanyStatus(status, companyId);

  return (
    <Box
      sx={{
        pl: 1,
        borderLeft: '4px solid',
        borderLeftColor: statusColor,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          fontWeight='medium'
          sx={{
            whiteSpace: 'normal',
            lineHeight: 1.2,
            fontSize: {
              xs: '0.875rem',
              sm: '1rem',
              md: '1.15rem',
            },
          }}
          variant='subtitle1'
        >
          {alternateName ? `${name} (${alternateName})` : name}
        </Typography>
        {address && (
          <Typography
            color='text.secondary'
            sx={{
              whiteSpace: 'normal',
              lineHeight: 1.2,
              fontSize: {
                xs: '0.75rem',
                sm: '0.8rem',
                md: '0.875rem',
              },
            }}
            variant='body2'
          >
            {address}
          </Typography>
        )}
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mt: 0.5,
        }}
        >
          <Chip
            label={getLabelForCompanyStatus({
              status,
              companyId,
              statusDescription,
              activeLabel: t('active'),
              draftLabel: t('draft'),
              unknownLabel: t('unknown'),
            })}
            size='small'
            sx={{
              ...DEFAULT_CHIP_SX,
              flexShrink: 1,
              borderColor: statusColor,
            }}
            variant='outlined'
          />
          {officeType && (
            <Chip
              label={t(`type.${officeType.toLowerCase()}`)}
              size='small'
              sx={DEFAULT_CHIP_SX}
              variant='outlined'
            />
          )}
          {erpId && (
            <Chip
              label={erpId}
              size='small'
              sx={DEFAULT_CHIP_SX}
              variant='outlined'
            />
          )}
          {crmId && (
            <Chip
              label={crmId}
              size='small'
              sx={DEFAULT_CHIP_SX}
              variant='outlined'
            />
          )}
        </Box>
      </Box>
      {actions && (
        <Box sx={{
          width: 100,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
};

export { SearchResultItemLayout };
